import { isChatStatusBusy, useStores } from '@littlebird/sdk';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { format } from 'date-fns/format';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import { cn } from '@/lib/utils';

import { AgentAvatar } from './agents-avatar';
import {
  agentsStore,
  LAUNCHED_AGENT_NAME,
  launchDraftId,
  type LaunchRecord,
  ROUTED_SEND_HOLD_MS,
} from './agents.store';
import './agents.css';

const formatSentAt = (date: Date) => {
  const daysApart = differenceInCalendarDays(new Date(), date);
  if (daysApart <= 0) return format(date, 'h:mm a');
  if (daysApart === 1) return 'yesterday';
  if (daysApart < 7) return format(date, 'EEEE');
  return format(date, 'M/d/yy');
};

const MAX_TITLE_CHARS = 72;

const clipTitle = (query: string) =>
  query.length > MAX_TITLE_CHARS
    ? `${query.slice(0, MAX_TITLE_CHARS).trimEnd()}…`
    : query;

const WorkingDots = () => (
  <span aria-hidden="true" className="flex items-center gap-0.5">
    <span className="size-1 animate-pulse rounded-full bg-foreground-muted" />
    <span className="size-1 animate-pulse rounded-full bg-foreground-muted [animation-delay:200ms]" />
    <span className="size-1 animate-pulse rounded-full bg-foreground-muted [animation-delay:400ms]" />
  </span>
);

type AgentsLaunchRowProps = {
  record: LaunchRecord;
  dimmed: boolean;
  entering?: boolean;
  onOpen: (chatId: string) => void;
  onUndo: () => void;
  onRetry: () => void;
};

export const AgentsLaunchRow = observer(
  ({
    record,
    dimmed,
    entering = false,
    onOpen,
    onUndo,
    onRetry,
  }: AgentsLaunchRowProps) => {
    const agent = record.agentId
      ? agentsStore.getById(record.agentId)
      : undefined;
    const { threads, drafts } = useStores();
    const held = agentsStore.heldLaunchId === record.id;
    const unnamed = agent?.name === LAUNCHED_AGENT_NAME;
    const naming =
      unnamed &&
      record.chatId !== undefined &&
      isChatStatusBusy(threads.get(record.chatId)?.status);
    const sentAt = formatSentAt(new Date(record.sentAt));

    const queued = record.sendState === 'pending' && !held;
    const failed = record.sendState === 'failed';
    const retainedFiles = failed
      ? (drafts.getDraft(launchDraftId(record.id))?.files.length ?? 0)
      : 0;
    const statusPrefix = failed
      ? agent
        ? 'Could not send to'
        : 'Could not start'
      : !agent
        ? 'Finding who takes this'
        : queued
          ? 'Waiting for'
          : record.kind === 'routed'
            ? 'Routed to'
            : naming
              ? 'Started a new agent'
              : unnamed
                ? 'Started an agent that has not named itself'
                : 'Started';
    const statusName = agent && !unnamed ? agent.name : undefined;

    return (
      <XStack
        className={cn(
          'group/row isolate relative h-11 items-start gap-4 before:absolute before:-inset-x-3 before:-inset-y-2 before:-z-10 before:rounded-xl before:bg-surface-hover before:opacity-0',
          record.chatId && 'hover:before:opacity-100',
          dimmed && 'opacity-60',
          entering && 'agents-row-enter'
        )}
        onClick={() => record.chatId && onOpen(record.chatId)}
      >
        <span className="relative mt-px shrink-0">
          {agent && !unnamed ? (
            <AgentAvatar bird={agent.bird} className="size-7" />
          ) : (
            <span
              aria-hidden="true"
              className={cn(
                'block size-7 rounded-full border-[1.5px] border-dashed',
                agent
                  ? 'border-border'
                  : 'border-foreground-muted agents-ghost-spin'
              )}
            />
          )}
        </span>
        <YStack className="min-w-0 flex-1 gap-1.5">
          <button
            type="button"
            disabled={!record.chatId}
            onClick={(event) => {
              event.stopPropagation();
              if (record.chatId) onOpen(record.chatId);
            }}
            title={
              record.query.length > MAX_TITLE_CHARS ? record.query : undefined
            }
            className="min-w-0 truncate text-left text-sm.5 font-medium leading-5 text-foreground focus-ring disabled:cursor-default"
          >
            {clipTitle(record.query)}
          </button>
          <XStack className="h-4 min-w-0 items-center gap-1.5 text-xs.5 leading-4">
            <span className="shrink-0 text-foreground-muted">
              {statusPrefix}
            </span>
            {statusName && (
              <span className="truncate font-medium text-foreground-subtle">
                {statusName}
              </span>
            )}
            {!failed && (!agent || naming) && <WorkingDots />}
            {held && (
              <span
                aria-hidden="true"
                className="ml-1 h-[3px] w-11 shrink-0 overflow-hidden rounded-full bg-surface-active"
              >
                <span
                  className="agents-hold-drain block h-full bg-foreground-muted"
                  style={
                    {
                      '--agents-hold-ms': `${ROUTED_SEND_HOLD_MS}ms`,
                    } as React.CSSProperties
                  }
                />
              </span>
            )}
            {retainedFiles > 0 && (
              <span className="shrink-0 text-foreground-muted">
                · {retainedFiles} {retainedFiles === 1 ? 'file' : 'files'}
              </span>
            )}
            {failed && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onRetry();
                }}
                className="ml-1 shrink-0 rounded-full border border-border bg-surface px-2.5 py-px text-[11px] font-medium text-foreground hover:bg-surface-hover focus-ring"
              >
                Retry
              </button>
            )}
            {held && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onUndo();
                }}
                className="ml-1 shrink-0 rounded-full border border-border bg-surface px-2.5 py-px text-[11px] font-medium text-foreground hover:bg-surface-hover focus-ring"
              >
                Undo · start fresh
              </button>
            )}
          </XStack>
        </YStack>
        {agent && !held && (
          <span className="relative shrink-0 pt-[3px] text-[11px] text-foreground-muted">
            <span className="group-hover/row:invisible group-has-[:focus-visible]/row:invisible">
              {sentAt}
            </span>
            <span className="invisible absolute right-0 top-[3px] inline-flex items-center gap-1 whitespace-nowrap text-foreground-subtle group-hover/row:visible group-has-[:focus-visible]/row:visible">
              Open
              <Icons.arrowUpRight className="size-3" />
            </span>
          </span>
        )}
      </XStack>
    );
  }
);
